/**
 * Block-for-block skeleton for /billing/family-balance (Plan 09).
 *
 * Mirrors the live page layout: header + branching switcher, then
 * three stacked surfaces:
 *
 *   1. Balance card — large $ figure + auto-reload toggle row + top-up CTA
 *   2. Payment methods card — list of saved methods + add CTA
 *   3. Ledger table — filter chips + a 5-row placeholder
 *
 * Per the CLAUDE.md "Loading states" guidance, never a `Loading...`
 * string. The skeleton must layout the same as the live page so the
 * swap is imperceptible.
 */

import { Card, CardContent, CardHeader } from '@ordo/ui/card'
import { Skeleton } from '@ordo/ui/skeleton'

export function BillingFamilyBalanceSkeleton() {
  return (
    <div
      className="flex h-full flex-col gap-6 overflow-y-auto p-8"
      aria-busy="true"
      aria-label="Loading family balance">
      {/* Header — branching switcher + title */}
      <header className="flex flex-col gap-3">
        <div className="flex gap-2">
          <Skeleton className="h-8 w-24 rounded-full" />
          <Skeleton className="h-8 w-24 rounded-full" />
        </div>
        <div className="flex flex-col gap-2">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-4 w-96" />
        </div>
      </header>

      {/* Balance card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-10 w-48" />
          </div>
          <Skeleton className="h-10 w-32" />
        </CardHeader>
        <CardContent className="border-t [&:not(:first-child)]:pt-6">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1.5">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-64" />
            </div>
            <Skeleton className="h-6 w-12 rounded-full" />
          </div>
        </CardContent>
      </Card>

      {/* Payment methods card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-9 w-32" />
        </CardHeader>
        <CardContent className="grid gap-3">
          <PaymentMethodRowSkeleton />
          <PaymentMethodRowSkeleton />
        </CardContent>
      </Card>

      {/* Ledger table */}
      <Card>
        <CardHeader className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-9 w-32" />
          </div>
          <div className="flex gap-1.5">
            <Skeleton className="h-7 w-16 rounded-md" />
            <Skeleton className="h-7 w-20 rounded-md" />
            <Skeleton className="h-7 w-20 rounded-md" />
            <Skeleton className="h-7 w-24 rounded-md" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <LedgerRowSkeleton key={i} />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function PaymentMethodRowSkeleton() {
  return (
    <div className="flex items-center gap-3">
      <Skeleton className="h-10 w-14 rounded" />
      <div className="flex-1 space-y-1.5">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-3 w-24" />
      </div>
      <Skeleton className="h-8 w-20" />
    </div>
  )
}

function LedgerRowSkeleton() {
  return (
    <div className="flex items-center gap-3 border-b pb-3 last:border-b-0">
      <Skeleton className="h-6 w-16 rounded-full" />
      <div className="flex-1 space-y-1.5">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-3 w-32" />
      </div>
      <Skeleton className="h-5 w-20" />
    </div>
  )
}
